import React from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';

/**
 * PADSideNavbar — vertical stage list for the PAD wizard.
 *
 * Each section object may carry a `comingSoon` boolean. When true, the row is
 * greyed out with a hover tooltip ("Coming soon") instead of the usual lock
 * icon. This is used by the CYP ADHD PAD where stages beyond Review of
 * Assessment are visible placeholders until requirements are confirmed.
 *
 * The row for the stage already on screen does not respond to clicks. Every
 * PAD page's navigateToSection switches its buttons to "Loading...", runs the
 * section-exit save and paragraph generation, then navigates to the chosen
 * stage's URL. When that URL is the one already open, the router has nothing
 * to do, the page never unmounts, and the loading state is never cleared, so
 * the Back and Next buttons stayed on "Loading..." until a refresh (tester
 * feedback, Oct 2026). It also spent a paragraph generation for no change.
 * Swallowing the click here fixes every PAD page at once, adult and CYP.
 */
function PADSideNavbar({ sections, currentSectionIndex, navigateToSection, isNavigating = false }) {
  return (
    <div className="w-96 ml-8 mt-8 h-full p-5 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-start gap-4 inline-flex sticky top-8 shrink-0">
      <div className="self-stretch text-gray-500 text-sm font-bold leading-tight">Stages</div>
      <div className={`text-left self-stretch flex-col justify-start items-start gap-1 flex overflow-y-auto ${isNavigating ? 'opacity-60 pointer-events-none' : ''}`}>
        {sections.map((section, index) => {
          const isActive = currentSectionIndex === index;
          const isCompleted = section.status === "completed";
          const isLocked = !section.isUnlocked;
          const isComingSoon = Boolean(section.comingSoon);
          const tooltipId = isComingSoon ? `pad-coming-soon-${index}` : undefined;
          // Locked rows and the row already on screen go nowhere (see the
          // component comment); a busy sidebar accepts no clicks at all.
          const isClickable = !isLocked && !isNavigating && !isActive;

          return (
            <React.Fragment key={index}>
              <div
                data-tooltip-id={tooltipId}
                aria-current={isActive ? 'step' : undefined}
                className={`self-stretch h-9 px-3 py-2 
                  transition-all duration-300 ease-in-out
                  ${isActive ? 'font-bold text-gray-900 border-l-2 border-violet-500' : 'border-l-2 border-transparent'} 
                  ${isCompleted ? 'bg-emerald-50' : ''} 
                  ${isLocked || isNavigating ? 'bg-gray-200 rounded-lg cursor-not-allowed' : ''} 
                  ${isClickable ? 'cursor-pointer hover:bg-gray-100' : ''} 
                  ${isActive && !isLocked && !isNavigating ? 'cursor-default' : ''} 
                  justify-start items-center gap-2 inline-flex`}
                onClick={() => isClickable && navigateToSection(index)}
              >
                <div className={`grow shrink basis-0 
                  transition-colors duration-300 ease-in-out
                  ${isActive ? 'text-gray-900' : 'text-gray-500'} 
                  text-sm font-medium leading-tight`}>
                  {section.sectionTitle}
                </div>
                <div className="transition-opacity duration-300 ease-in-out">
                  {isLocked && !isComingSoon ? (
                    <LockClosedIcon className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  ) : isCompleted && (
                    <CheckIcon className="w-4 h-4 text-green-500" aria-hidden="true" />
                  )}
                </div>
              </div>
              {isComingSoon && (
                <Tooltip
                  id={tooltipId}
                  place="right"
                  className="z-50 text-xs font-semibold"
                  style={{
                    maxWidth: '200px',
                    whiteSpace: 'normal',
                    backgroundColor: 'rgba(34, 34, 34, 1)',
                    borderRadius: '10px',
                    color: '#fff',
                  }}
                >
                  Coming soon
                </Tooltip>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default PADSideNavbar;